import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { UserWithModules, UpdateUserModulesRequest } from '../models/user-modules.model';
import {
  UsersApiService,
  CreateUserRequest as GeneratedCreateUserRequest,
  DeleteUserDto,
} from '@core/api/services/users-api.service';
import {
  PaginatedResponseOfPeopleListDto,
  PeopleListDto,
} from '@core/api/services/people-api.service';
import { CreatePersonRequest } from '@core/api/generated/api-client';

/**
 * User interface - represents a system user
 */
export interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  documentType: number;
  documentNumber: string;
  unitId: string;
  ministry?: number | null;
  ministryAppointment?: number | null;
  assignments: number[];
  contributions: ContributionData[];
  modules: ModuleData[];
}

export interface ContributionData {
  unitId: string;
  area: number;
  from: string;
  to?: string | null;
}

export interface ModuleData {
  moduleId: number;
  moduleName: string;
  enabled: boolean;
  canEdit: boolean;
  canApprove: boolean;
}

export interface UserResponse {
  id: string;
  email: string;
  emailVerified: boolean;
  enabled: boolean;
  hasLocalUser?: boolean;
  lastActivityAtUtc?: string | null;
  linkedMember?: LinkedMemberSummary | null;
  roles?: string[];
  roleAssignments?: UserRoleAssignmentSummary[];
  source?: string | null;
  username: string;
}

export interface LinkedMemberSummary {
  personId: string;
  fullName: string;
  hasMembershipUnit: boolean;
  activeMinistryName?: string | null;
  activeMinistryUnitId?: string | null;
  activeMinistryUnitName?: string | null;
}

export interface UserRoleAssignmentSummary {
  role: UserRole | string;
  organizationUnitId: string | null;
  organizationUnitName: string | null;
  organizationUnitLevelId?: number | null;
}

export interface UserBasicDetails {
  id?: string;
  email?: string;
  username?: string;
  fullName?: string | null;
  documentNumber?: string | null;
  membershipUnitName?: string | null;
  hasLocalUser?: boolean;
  lastActivityAtUtc?: string | null;
}

export type UserRole = 'Member' | 'Servant' | 'UnitManager' | 'MinisterialManager';

export interface UserRoleAssignmentDto {
  id: number;
  role: UserRole;
  organizationUnitId: string | null;
  organizationUnitName: string | null;
  organizationUnitLevelId?: number | null;
  includeDescendants: boolean;
  validFrom: string | null;
  validTo: string | null;
}

export interface UpsertUserRoleAssignmentDto {
  role: UserRole;
  organizationUnitId: string | null;
  includeDescendants: boolean;
  validFrom: string | null;
  validTo: string | null;
}

/**
 * Paginated response interface for users
 */
export interface PaginatedUsersResponse {
  items: UserResponse[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Query parameters for fetching users
 */
export interface GetUsersParams {
  page?: number;
  pageSize?: number;
  searchText?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  hasLocalUser?: boolean | null;
  isActive?: boolean | null;
  ministryId?: number | null;
  ministryUnitId?: string | null;
  membershipUnitId?: string | null;
  role?: UserRole | null;
  roleUnitId?: string | null;
}

/**
 * Request interface for creating a new user
 */
export interface CreateUserRequest {
  memberId: string;
  username: string;
  email?: string;
}

export interface UserActivitySummary {
  lastActivity: UserActivityItem | null;
  totalCount: number;
  last7DaysCount: number;
  last30DaysCount: number;
  countsByActionType: UserActivityTypeCount[];
}

export interface UserActivityItem {
  id: string;
  timestampUtc: string;
  actionType: string;
  entity: string;
  entityId?: string | null;
  details: string;
  isSuccessful: boolean;
  errorMessage?: string | null;
  previousData?: string | null;
  newData?: string | null;
}

export interface UserActivityTypeCount {
  actionType: string;
  count: number;
}

export interface PaginatedUserActivityResponse {
  items: UserActivityItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface GetUserActivityParams {
  page?: number;
  pageSize?: number;
  actionType?: string | null;
  from?: string | null;
  to?: string | null;
}

export type EligiblePerson = PeopleListDto;

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly apiUrl = `${environment.apiUrl}/Users`;
  private readonly meApiUrl = `${environment.apiUrl}/Me`;

  constructor(
    private http: HttpClient,
    private readonly usersApi: UsersApiService,
  ) { }

  /**
   * Retrieves all system users with optional pagination and filtering
   * @param params Query parameters for pagination and filtering
   * @returns Observable of paginated users response
   */
  getAllUsers(params?: GetUsersParams): Observable<PaginatedUsersResponse> {
    const defaults: GetUsersParams = {
      page: 1,
      pageSize: 50,
      searchText: ''
    };
    const mergedParams = { ...defaults, ...params };

    return this.http.get<PaginatedUsersResponse>(this.apiUrl, {
      params: {
        SearchTerm: mergedParams.searchText?.trim() || '',
        Page: mergedParams.page ?? defaults.page!,
        PageSize: mergedParams.pageSize ?? defaults.pageSize!,
        ...Object.fromEntries(Object.entries({
          SortBy: mergedParams.sortBy,
          SortDirection: mergedParams.sortDirection,
          IsActive: mergedParams.isActive,
          MinistryId: mergedParams.ministryId,
          MinistryUnitId: mergedParams.ministryUnitId,
          MembershipUnitId: mergedParams.membershipUnitId,
          Role: mergedParams.role,
          RoleUnitId: mergedParams.roleUnitId
        }).filter(([, value]) => value !== null && value !== undefined)) as Record<string, string | number | boolean>,
        ...(mergedParams.hasLocalUser !== null && mergedParams.hasLocalUser !== undefined
          ? { HasLocalUser: mergedParams.hasLocalUser }
          : {})
      }
    });
  }

  /**
   * Retrieves a single user by ID
   * @param id User ID
   * @returns Observable of user data
   */
  getUserById(id: string): Observable<UserBasicDetails> {
    return this.http.get<UserBasicDetails>(`${this.apiUrl}/${id}`);
  }

  getCurrentUserDetail(): Observable<UserBasicDetails> {
    return this.http.get<UserBasicDetails>(`${this.meApiUrl}/user-detail`);
  }

  /**
   * Get user with modules information for editing (optimized - single request)
   * @param id User ID
   * @returns Observable of user with modules
   */
  getUserWithModulesForEdit(id: string): Observable<UserWithModules> {
    return this.http.get<UserWithModules>(`${this.apiUrl}/${id}/modules-edit`);
  }

  /**
  * Creates a new user
  * @param user User data to create
  * @returns Observable of void
  */
  createUser(user: CreateUserRequest): Observable<void> {
    return this.usersApi.createUser(new GeneratedCreateUserRequest(user));
  }

  associateMember(userId: string, personId: string): Observable<void> {
    return this.usersApi.associateMember(userId, personId);
  }

  createMember(userId: string, member: CreatePersonRequest): Observable<string> {
    return this.http.post<string>(`${this.apiUrl}/${userId}/member`, member);
  }

  /**
   * Updates an existing user
   * @param id User ID
   * @param user User data to update
   * @returns Observable of updated user
   */
  updateUser(id: string, user: Partial<User>): Observable<User> {
    return this.http.put<User>(`${this.apiUrl}/${id}`, user);
  }

  updateUserEmail(id: string, email: string): Observable<void> {
    return this.usersApi.updateUserEmail(id, email);
  }

  updateCurrentUserEmail(email: string): Observable<void> {
    return this.http.put<void>(`${this.meApiUrl}/email`, JSON.stringify(email), {
      headers: { 'Content-Type': 'application/json' }
    });
  }

  /**
   * Updates the active/inactive status of a user
   * @param id User ID
   * @param isActive New status value
   * @returns Observable of void (204 No Content)
   */
  updateUserStatus(id: string, isActive: boolean): Observable<void> {
    return this.usersApi.updateStatus(id, isActive);
  }

  getEligiblePeople(searchTerm: string, page = 1, pageSize = 10): Observable<PaginatedResponseOfPeopleListDto> {
    return this.http.get<PaginatedResponseOfPeopleListDto>(`${this.apiUrl}/eligible-people`, {
      params: {
        searchTerm,
        page,
        pageSize
      }
    });
  }

  getRoleAssignments(userId: string): Observable<UserRoleAssignmentDto[]> {
    return this.http.get<UserRoleAssignmentDto[]>(`${this.apiUrl}/${userId}/role-assignments`);
  }

  getCurrentUserRoleAssignments(): Observable<UserRoleAssignmentDto[]> {
    return this.http.get<UserRoleAssignmentDto[]>(`${this.meApiUrl}/role-assignments`);
  }

  getUserActivitySummary(userId: string): Observable<UserActivitySummary> {
    return this.http.get<UserActivitySummary>(`${this.apiUrl}/${userId}/activity-summary`);
  }

  getUserActivity(userId: string, params: GetUserActivityParams = {}): Observable<PaginatedUserActivityResponse> {
    return this.http.get<PaginatedUserActivityResponse>(`${this.apiUrl}/${userId}/activity`, {
      params: {
        Page: params.page ?? 1,
        PageSize: params.pageSize ?? 10,
        ...(params.actionType ? { ActionType: params.actionType } : {}),
        ...(params.from ? { From: params.from } : {}),
        ...(params.to ? { To: params.to } : {})
      }
    });
  }

  updateRoleAssignments(userId: string, assignments: UpsertUserRoleAssignmentDto[]): Observable<UserRoleAssignmentDto[]> {
    return this.http.put<UserRoleAssignmentDto[]>(`${this.apiUrl}/${userId}/role-assignments`, assignments);
  }

  /**
   * Update user modules and permissions
   * @param id User ID
   * @param request Update request with modules data
   * @returns Observable of void (204 No Content)
   */
  updateUserModules(id: string, request: UpdateUserModulesRequest): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${id}/modules`, request);
  }

  /**
   * Deletes a user
   * @param id User ID
   * @param deletionReason Reason for deleting the user
   * @returns Observable of deletion response
   */
  deleteUser(id: string, deletionReason: string): Observable<void> {
    const request = new DeleteUserDto({ deletionReason });
    return this.usersApi.deleteUser(id, request);
  }
}
